import { Theme, Typography, useTheme } from "@material-ui/core";
import {
  BluetoothAudio,
  BluetoothDisabled,
  Help,
  Power,
  PowerOff,
} from "@material-ui/icons";
import { formatDistanceToNow } from "date-fns";
import { memo, useMemo } from "react";
import { VehicleIgnitionStatus } from "../../../common/data/service-proxies";

const getStatusColor = (status: string, theme: Theme) => {
  switch (status) {
    case "OFF":
      return theme.palette.grey[900];
    case "ON":
      return theme.palette.info.main;
    case "D":
      return theme.palette.success.main;
    case "SB":
      return theme.palette.warning.main;
    default:
      return "auto";
  }
};

interface Props {
  eldConnected?: boolean | null;
  isPlugged?: boolean | null;
  ignitionStatus?: VehicleIgnitionStatus | null;
  isPluggedUpdateTime?: string | null;
  ignitionStatusUpdateTime?: string | null;
}

export const VehicleStatusHeader = memo(
  ({
    eldConnected,
    isPlugged,
    isPluggedUpdateTime,
    ignitionStatusUpdateTime,
    ignitionStatus,
  }: Props) => {
    const theme = useTheme<Theme>();

    const ignitionStatusUpdateToNow = useMemo(() => {
      if (!ignitionStatusUpdateTime) return null;
      return formatDistanceToNow(new Date(ignitionStatusUpdateTime), {
        addSuffix: true,
      });
    }, [ignitionStatusUpdateTime]);

    const pluggedStatusUpdateToNow = useMemo(() => {
      if (!isPluggedUpdateTime) return null;
      return formatDistanceToNow(new Date(isPluggedUpdateTime), {
        addSuffix: true,
      });
    }, [isPluggedUpdateTime]);

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
          gap: "12px",
          alignItems: "center",
          paddingTop: "4px",
          paddingLeft: "10px",
          paddingRight: "10px",
        }}
        className="info-window-content-header"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "4px",
            alignItems: "center",
          }}
        >
          <div>
            {eldConnected ? (
              <Typography variant="body2">
                <BluetoothAudio
                  fontSize="small"
                  style={{ color: theme.palette.success.main }}
                />
              </Typography>
            ) : eldConnected === null ? (
              <Typography variant="body2">
                <Help
                  fontSize="small"
                  style={{ color: theme.palette.warning.main }}
                />
              </Typography>
            ) : (
              <Typography variant="body2">
                <BluetoothDisabled
                  fontSize="small"
                  style={{ color: theme.palette.error.main }}
                />
              </Typography>
            )}
          </div>
          <p>
            {eldConnected
              ? "Connected"
              : eldConnected === null
              ? "Unknown"
              : "Disconnected"}
          </p>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "4px",
            alignItems: "center",
          }}
        >
          <div>
            {ignitionStatus === VehicleIgnitionStatus.IgnitionOnDriving ? (
              <div
                className="duty-status"
                style={{
                  padding: "2px 4px",
                  borderRadius: "4px",
                  backgroundColor: getStatusColor("D", theme),
                  color: theme.palette.getContrastText(
                    getStatusColor("D", theme)
                  ),
                }}
              >
                DR
              </div>
            ) : ignitionStatus === VehicleIgnitionStatus.IgnitionOff ? (
              <div
                className="duty-status"
                style={{
                  padding: "2px 4px",
                  borderRadius: "4px",
                  backgroundColor: getStatusColor("OFF", theme),
                  color: theme.palette.getContrastText(
                    getStatusColor("OFF", theme)
                  ),
                }}
              >
                OF
              </div>
            ) : ignitionStatus === VehicleIgnitionStatus.IgnitionOnIdle ? (
              <div
                className="duty-status"
                style={{
                  padding: "2px 4px",
                  borderRadius: "4px",
                  backgroundColor: getStatusColor("SB", theme),
                  color: theme.palette.getContrastText(
                    getStatusColor("SB", theme)
                  ),
                }}
              >
                ID
              </div>
            ) : (
              "Unknown status"
            )}
          </div>
          <p>{ignitionStatusUpdateToNow ? ignitionStatusUpdateToNow : ""}</p>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "4px",
            alignItems: "center",
          }}
        >
          <div>
            {isPlugged ? (
              <Typography variant="body2">
                <Power
                  fontSize="small"
                  style={{ color: theme.palette.success.main }}
                />
              </Typography>
            ) : isPlugged === null ? (
              <Typography variant="body2">
                <Help
                  fontSize="small"
                  style={{ color: theme.palette.warning.main }}
                />
              </Typography>
            ) : (
              <Typography variant="body2">
                <PowerOff
                  fontSize="small"
                  style={{ color: theme.palette.error.main }}
                />
              </Typography>
            )}
          </div>
          <p>
            {pluggedStatusUpdateToNow
              ? pluggedStatusUpdateToNow
              : isPlugged
              ? "Plugged"
              : "Unplugged"}
          </p>
        </div>
      </div>
    );
  }
);
